import { Fix64, GearItem, GearOwner, GearStat, GearTarget, PlayerProfile, StatUse, gearStatUse, primaryOn, primaryStatOn, raiseCap, raiseCostAt, sameOwner } from "@highmarch/domain";
import {
  LoadoutScope, OwnerView, Readiness, ReadinessState, SaveRecord,
  compareOn, isNewGear, itemScore, markGearSeen, readinessFor, readinessTitle,
} from "@highmarch/app";

/**
 * The Armoury page's decisions, kept apart from its DOM so they can be tested headless: how the owner strip groups
 * owners, which owner the page opens on, what the "For X" strip lists (or the "Get stronger" offers that replace it),
 * and what the readiness dock says. Pure over the profile, except `openSeen`, which says so.
 */

export type ArmScope = LoadoutScope & { readonly level: number; readonly roster: readonly boolean[] };

/** The owner strip's three groups: the next level's line-up (the fortress first), the other owned types, the locked ones. */
export interface OwnerGroups { readonly lineup: readonly OwnerView[]; readonly bench: readonly OwnerView[]; readonly locked: readonly OwnerView[] }

export function ownerGroups(views: readonly OwnerView[]): OwnerGroups {
  return {
    lineup: views.filter((v) => v.isBase || (v.seat && v.owned)),
    bench: views.filter((v) => !v.isBase && v.owned && !v.seat),
    locked: views.filter((v) => !v.owned),
  };
}

/** The dot on an owner tile: how many of its sockets the stash can improve. */
export function betterSockets(v: OwnerView): number {
  return v.owned ? v.sockets.filter((s) => s.better > 0).length : 0;
}

/** Sockets the best loadout for the level would change on this owner (a better item fits there now). */
export function plannedSockets(v: OwnerView): number {
  return v.sockets.filter((s) => s.planned !== null).length;
}

export const ownerKey = (o: GearOwner): string => (o.target === GearTarget.Base ? "base" : `t${o.towerIndex}`);

/** The owner strip left to right (line-up, bench, locked), as `ownerStripHtml` lays it out: the order a swipe walks. */
export function stripOrder(views: readonly OwnerView[]): OwnerView[] {
  const g = ownerGroups(views);
  return [...g.lineup, ...g.bench, ...g.locked];
}

/**
 * The owner a swipe on the paper doll moves to: one tile along the strip (`step` +1 = next, to the right), or null
 * past either end (no wrap: the strip does not wrap either). Locked types are included, as a tap on their tile shows
 * their locked stage too.
 */
export function neighbourOwner(views: readonly OwnerView[], selected: string, step: 1 | -1): string | null {
  const order = stripOrder(views);
  const at = order.findIndex((v) => ownerKey(v.owner) === selected);
  if (at < 0) return null;
  const next = order[at + step];
  return next === undefined ? null : ownerKey(next.owner);
}

/**
 * Which owner the page opens on — a line-up tower before the fortress, since the towers are what the player drafts.
 * The one picked earlier this session (if still owned), else the first one the stash improves, else the first line-up
 * tower (the fortress when it is alone).
 */
export function initialOwner(views: readonly OwnerView[], remembered: string | null): OwnerView {
  const g = ownerGroups(views);
  const order = [...g.lineup.filter((v) => !v.isBase), ...g.lineup.filter((v) => v.isBase)];
  const kept = remembered === null ? undefined : views.find((v) => ownerKey(v.owner) === remembered && v.owned);
  if (kept) return kept;
  return order.find((v) => betterSockets(v) > 0) ?? order[0] ?? views[0];
}

/** One item in the "For X" strip: its gear-power gain on the selected owner and whether it is NEW. */
export interface ForItem { readonly item: GearItem; readonly delta: number; readonly isNew: boolean }

export interface ForStrip {
  /** Items the owner can wear (its kind, level met), not on it now, best gain first. */
  readonly items: readonly ForItem[];
  /** Of those, better ones in the stash, and better ones worn by a bench tower (Equip best may leave those). */
  readonly betterStash: number;
  readonly betterBench: number;
}

export function forOwner(profile: PlayerProfile, view: OwnerView, isNew: (g: GearItem) => boolean, isLineup: (o: GearOwner) => boolean): ForStrip {
  const B = profile.balance;
  const items: ForItem[] = [];
  if (view.owned) {
    for (const g of profile.gear) {
      if (B.gear.slots[g.slot].target !== view.owner.target) continue;
      if (g.equippedTo !== null && sameOwner(g.equippedTo, view.owner)) continue;
      const c = compareOn(profile, g, view.owner);
      if (c.canWear !== true) continue;
      items.push({ item: g, delta: c.power, isNew: isNew(g) });
    }
  }
  items.sort((a, b) => b.delta - a.delta || Number(b.isNew) - Number(a.isNew) || b.item.rarity - a.item.rarity || b.item.id - a.item.id);
  const better = items.filter((f) => f.delta > 0);
  return {
    items,
    betterStash: better.filter((f) => f.item.equippedTo === null).length,
    betterBench: better.filter((f) => f.item.equippedTo !== null && !isLineup(f.item.equippedTo)).length,
  };
}

/**
 * The "n new" tag on a socket: items NEW this visit that the owner can wear there and that beat what it wears (or fill
 * it empty) — the "For X" strip's own items, by slot index. Items worn by another line-up owner are left out: moving
 * those is a trade-off for the level, not an upgrade (same split as `betterStash` / `betterBench`).
 */
export function newPerSocket(items: readonly ForItem[], isLineup: (o: GearOwner) => boolean): ReadonlyMap<number, number> {
  const out = new Map<number, number>();
  for (const f of items) {
    if (!f.isNew || f.delta <= 0) continue;
    if (f.item.equippedTo !== null && isLineup(f.item.equippedTo)) continue;
    out.set(f.item.slot, (out.get(f.item.slot) ?? 0) + 1);
  }
  return out;
}

/** "Get stronger" (graft 1) shows at most this many +1 upgrades. */
export const MAX_OFFERS = 3;

/** A one-level raise on a worn item (gear v2): the gear it adds, its essence cost, and the primary before and after. */
export interface UpgradeOffer {
  readonly item: GearItem;
  readonly gain: number;
  /** Forge Essence. */
  readonly cost: number;
  readonly affordable: boolean;
  readonly before: Fix64;
  readonly after: Fix64;
  /** The stat the primary counts as on this owner (gear.json retarget: a totem's damage is range there). */
  readonly stat: GearStat;
  /** How that stat counts on this owner: Ascended reads "tier 4". */
  readonly use: StatUse;
}

/**
 * The owner's worn items that can still go up a level, most gear per essence first. A raise that moves no stat (an item
 * at its stat's top) or raises one this owner never uses is no way to get stronger.
 */
export function upgradeOffers(profile: PlayerProfile, view: OwnerView, max = MAX_OFFERS): UpgradeOffer[] {
  const B = profile.balance;
  const offers = view.sockets.flatMap((s): UpgradeOffer[] => {
    const it = s.worn;
    if (it === null || it.level >= raiseCap(profile, it)) return [];
    const next = { ...it, level: it.level + 1 };
    const before = primaryOn(B, it, view.owner), after = primaryOn(B, next, view.owner), stat = primaryStatOn(B, it, view.owner), use = gearStatUse(B, view.owner, stat);
    if (after.eq(before) || use === StatUse.None) return [];
    const cost = raiseCostAt(B, it.rarity, it.level);
    return [{ item: it, gain: itemScore(B, next) - itemScore(B, it), cost, affordable: profile.forge.essence >= cost, before, after, stat, use }];
  });
  const worth = (o: UpgradeOffer) => o.gain / Math.max(1, o.cost);
  return offers.sort((a, b) => worth(b) - worth(a) || a.cost - b.cost || a.item.slot - b.item.slot).slice(0, max);
}

/** What the strip under the paper doll shows. */
export type StripMode = "locked" | "empty" | "items" | "stronger";

export function stripMode(profile: PlayerProfile, view: OwnerView, strip: ForStrip, forceStronger: boolean): StripMode {
  if (!view.owned) return "locked";
  if (profile.gear.length === 0) return "empty";
  if (forceStronger) return "stronger";
  return strip.betterStash > 0 || plannedSockets(view) > 0 ? "items" : "stronger";
}

/** Dock tone per readiness state, matching the hub strip's colours. */
export type DockTone = "neutral" | "green" | "amber" | "red";
const TONE: Record<ReadinessState, DockTone> = { noGearNeeded: "neutral", ready: "green", equip: "amber", underGeared: "red" };
const PERCENT = 100;

export interface DockModel {
  readonly readiness: Readiness;
  /** `readinessTitle`, word for word as the hub strip says it. */
  readonly title: string;
  readonly tone: DockTone;
  /** The gear meter as the hub strip shows it: percent of the level's target (capped at 100). */
  readonly showMeter: boolean;
  readonly nowPct: number;
  /** Where the Ready line sits (balance readiness.readyGearPercent). */
  readonly readyPct: number;
  /** The first level that needs gear, while this one does not (null once gear counts). */
  readonly gearFrom: number | null;
}

const pctOf = (have: number, target: number): number => (target <= 0 ? PERCENT : Math.round(Math.min(1, have / target) * PERCENT));

export function dockModel(profile: PlayerProfile, scope: ArmScope): DockModel {
  const B = profile.balance;
  const r = readinessFor(profile, scope.level, { roster: scope.roster });
  const gearFrom = r.state === "noGearNeeded" ? B.readiness.gearRequiredFrom : null;
  return {
    readiness: r, title: readinessTitle(r, gearFrom), tone: TONE[r.state],
    showMeter: r.gearRequired && r.gear.target > 0,
    nowPct: pctOf(r.gear.have, r.gear.target),
    readyPct: B.readiness.readyGearPercent, gearFrom,
  };
}

/**
 * NEW on this visit: reads the watermark as it is when the page opens, then marks everything held as seen at once
 * (SheetGuard can remove the sheet without a close handler). Changes the save; `changed` says whether to persist.
 */
export function openSeen(save: SaveRecord, items: readonly GearItem[]): { readonly isNew: (g: GearItem) => boolean; readonly changed: boolean; readonly since: number | undefined } {
  const seen = { gearSeenUpTo: save.gearSeenUpTo };
  const changed = markGearSeen(save, items);
  return { isNew: (g) => isNewGear(g, seen), changed, since: seen.gearSeenUpTo };
}
